import {
    useContext, useMemo, useCallback, useRef
} from 'react';
import {
    v4 as uuidv4
} from 'uuid';
import {
    useWebSocket
} from 'builders/builderOpcode/hooks/useSocket';
import {
    Context
} from 'shared/context/GlobalState';

// Same WS endpoint the legacy JsonOpcodeExecute uses (A-0008.05).
const EXECUTE_URL = 'api/ws/projecto/config/api/opcode/execute';

/**
 * useOpcodeStream — clean wrapper over the M20 OpCode WebSocket hook.
 *
 * The underlying useWebSocket maintains a long-lived WS connection that
 * accepts START_PROCESS messages and emits CPIEResponse events. This hook
 * keeps that contract intact but exposes a smaller surface to the dock:
 *
 *   const { events, isRunning, error, taskId, run, connected } =
 *     useOpcodeStream({ orgId, subOrgId });
 *
 *   run(opcodeId, inputs, { emitTokens, bufferSentences, debug, memoryLogId })
 *     -> sends a START_PROCESS frame; events update as the stream progresses.
 *
 * Each hook instance owns its own WS connection (sessionId is generated once
 * per mount) so two panels can run in parallel without colliding.
 */
export function useOpcodeStream({
    orgId, subOrgId
}) {
    const {
        user
    } = useContext(Context) || {};
    const sessionId = useMemo(() => uuidv4(), []);
    const connectionId = `${user?._id || 'anon'}-${orgId || 'noorg'}-${sessionId}`;

    const {
        sendMessage,
        messages,
        isExecuting,
        errorResult,
        taskId,
        isConnected
    } = useWebSocket(connectionId, EXECUTE_URL);

    // Capture the last opcode_id we ran so the renderer can label correctly.
    const lastOpcodeId = useRef(null);

    const run = useCallback((opcodeId, inputs, options = {}) => {
        if (!opcodeId) {
            console.warn('useOpcodeStream.run: opcodeId is required');
            return;
        }
        lastOpcodeId.current = opcodeId;
        const body = {
            opcode_id: opcodeId,
            org_id: orgId,
            sub_org_id: subOrgId,
            inputs: inputs || {},
            streaming: {
                emit_tokens: !!options.emitTokens,
                buffer_sentences: !!options.bufferSentences
            },
            debug: options.debug !== false,
            client_request_id: uuidv4()
        };
        if (options.memoryLogId) body.memory_log_id = options.memoryLogId;
        sendMessage(body);
    }, [orgId, subOrgId, sendMessage]);

    return {
        events: messages,
        isRunning: isExecuting,
        error: errorResult,
        taskId,
        connected: isConnected,
        run,
        lastOpcodeId: lastOpcodeId.current
    };
}
